import { clsx } from 'clsx'
import { forwardRef } from 'react'
import { View } from 'react-native'

import { HStack, Pressable, Text, VStack, type HStackProps } from '@/components'

export interface BottomSheetHeaderProps extends Omit<HStackProps, 'children'> {
  closeAccessibilityLabel?: string
  closeLabel?: string
  onClosePress?: () => void
  title: string
  titleAccessibilityLabel?: string
}

export const BottomSheetHeader = forwardRef<View, BottomSheetHeaderProps>(
  (
    {
      closeAccessibilityLabel: _closeAccessibilityLabel,
      closeLabel = 'Close',
      className,
      onClosePress,
      title,
      titleAccessibilityLabel,
      ...props
    },
    ref
  ) => {
    const closeAccessibilityLabel = _closeAccessibilityLabel ?? `Close ${title}`

    return (
      <HStack
        className={clsx(
          'h-10 flex-nowrap items-center border-b-[0.5px] border-b-brand-grey-400 bg-brand-black-400 pl-4',
          className
        )}
        {...props}
      >
        <VStack
          accessible
          accessibilityLabel={titleAccessibilityLabel ?? title}
          accessibilityRole='header'
          className='w-full shrink'
          focusable
          importantForAccessibility='yes'
          ref={ref}
          tabIndex={0}
        >
          <Text
            accessibilityElementsHidden
            aria-hidden
            importantForAccessibility='no-hide-descendants'
            className='h-10 font-poppinsSemiBold leading-10 text-white'
            size='lg'
          >
            {title}
          </Text>
        </VStack>
        <Pressable
          accessibilityLabel={closeAccessibilityLabel}
          accessibilityRole='button'
          className='h-10 items-center justify-center px-4'
          onPress={onClosePress}
        >
          <Text className='font-poppinsMedium text-xs text-white'>{closeLabel}</Text>
        </Pressable>
      </HStack>
    )
  }
)

BottomSheetHeader.displayName = 'BottomSheetHeader'
